import {
  useEffect,
  useRef,
  type FormEvent,
  type ReactElement,
  type RefObject,
} from 'react';
import { Button, TextField } from '@/components';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';

/**
 * `FE-07.1` — workspace creation. Chunk `D2`, agent C.
 *
 * **One form, two hosts, and only one of them is a `Dialog`** —
 * `entry-surfaces.md` **D4**, fixed:
 *
 *  - from the boards landing page's first-run empty state (J1 step 3), the form
 *    **replaces the `EmptyState`'s body in place**, with no dialog at all;
 *  - from the workspace switcher's *New workspace…* footer item, the same
 *    component is mounted inside a `Dialog`.
 *
 * It is one component because a form built twice is a form that behaves
 * differently in two places. It is **purely presentational** for the same
 * reason `ProfilePanel` is: a panel that fetches cannot be mounted twice.
 *
 * ── No surface of its own ───────────────────────────────────────────────
 * Both hosts are already opaque — `EmptyState` carries its own
 * `--color-surface-raised` sheet, and `Dialog`'s panel is `--color-surface`
 * — so this file renders no `CardSurface`. Wrapping it in one here would
 * double the radius and the shadow inside whichever host mounts it (§8.3 of
 * `D2-BRIEF.md`, and `EmptyState`'s own docstring, make the same point about
 * nesting an opaque sheet inside one).
 *
 * ── Uncontrolled, read through a ref ────────────────────────────────────
 * The single field is read from its own ref at submit time, the same
 * "no hooks means no *data* hooks" reading `SignInPanel` uses — a
 * `useState` here would exist only to shadow a value the DOM node already
 * holds. `autoFocus` is imperative for the same reason `TextField` cannot
 * take a plain `autoFocus` boolean and be trusted across re-renders: the
 * effect below runs once, on mount, so a parent re-rendering this component
 * with `autoFocus` still `true` does not keep stealing focus back from
 * whatever the member did next.
 */
export interface WorkspaceCreateFormProps {
  onSubmit: (name: string) => void;
  onCancel: () => void;
  busy?: boolean;
  /** The server's message, verbatim. Rendered through the field's own `error`. */
  error?: string;
  /**
   * Autofocus the name field on mount. The inline host passes `true`; the
   * `Dialog` host does not — `Dialog` owns initial focus through `initialFocus`,
   * and two things claiming it is a focus fight nobody wins.
   */
  autoFocus?: boolean;
  /**
   * The name field, exposed so a host that owns initial focus can aim at it.
   *
   * ⚠ **Added in D2's integration pass, and the workaround it replaces is the
   * reason it exists.** `Dialog`'s `initialFocus` wants a `RefObject` pointing
   * at a real element, and this component had no way to hand one out — so
   * `__root.tsx` wrapped the form in a `<div>` and synthesised a fake
   * `RefObject` whose `current` was a getter running
   * `container.querySelector('input')`. That works exactly as long as this form
   * has one input, and silently aims at the wrong control the day it has two.
   *
   * The two hosts split cleanly along this prop: the inline host focuses the
   * field itself with `autoFocus`, and the `Dialog` host passes this and lets
   * `Dialog` do it.
   */
  nameFieldRef?: RefObject<HTMLInputElement | null>;
  className?: string;
}

export function WorkspaceCreateForm({
  onSubmit,
  onCancel,
  busy,
  error,
  autoFocus = false,
  nameFieldRef,
  className,
}: WorkspaceCreateFormProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const nameRef = useRef<HTMLInputElement>(null);

  /*
   * One element, two refs: the internal one this component reads at submit
   * time and focuses on mount, and the optional one a host aims `initialFocus`
   * at. A callback ref is the only way to fill both without either host having
   * to know about the other's.
   */
  function setNameField(node: HTMLInputElement | null) {
    nameRef.current = node;
    if (nameFieldRef !== undefined) nameFieldRef.current = node;
  }

  // Runs once, on mount, deliberately — see the docstring above.
  useEffect(() => {
    if (autoFocus) {
      nameRef.current?.focus();
    }
  }, []);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // Mirrors `SignInPanel`'s own guard: `Button`'s click guard does not
    // cover Enter-to-submit, which bypasses the button entirely.
    if (busy === true) return;
    const trimmed = (nameRef.current?.value ?? '').trim();
    // An empty or whitespace-only name is a 422 the server would refuse
    // anyway — posting it is a round trip for nothing (§6.3 rule 4).
    if (trimmed === '') return;
    onSubmit(trimmed);
  }

  return (
    <form className={cn('flex flex-col gap-4', className)} onSubmit={handleSubmit}>
      <TextField
        ref={setNameField}
        label={t('workspaceSettings.create.nameLabel')}
        name="name"
        hint={t('workspaceSettings.create.nameHint')}
        autoComplete="off"
        required
        {...(error !== undefined ? { error } : {})}
      />

      <div className="flex items-center gap-2">
        <Button
          type="submit"
          variant="primary"
          busyLabel={t('workspaceSettings.create.submitBusy')}
          {...(busy !== undefined ? { busy } : {})}
        >
          {t('workspaceSettings.create.submit')}
        </Button>
        <Button type="button" variant="quiet" onClick={onCancel}>
          {t('workspaceSettings.create.cancel')}
        </Button>
      </div>
    </form>
  );
}
